class QuickViewDrawer extends MenuDrawer {
  constructor() {
    super();

    this.setClasses({
      open: 'quick-view--open',
      opening: 'quick-view--opening',
      closing: 'quick-view--closing'
    });

    this.addEventListener('close', () => {
      const drawerContent = this.querySelector('.quick-view__content');
      if (drawerContent) {
        drawerContent.innerHTML = '';
        drawerContent.classList.remove('hide-cover');
      }

      const quickView = this.querySelector('quick-view');
      if (quickView) {
        quickView.resetLoadState();
      }

      document.dispatchEvent(new CustomEvent('quickview:close'));
    });
  }
}
customElements.define('quick-view-drawer', QuickViewDrawer);

class QuickViewButton extends MenuDrawer {
  constructor() {
    super();

    this.addEventListener('click', () => {
      const wrapper = this.closest('.card-wrapper');
      if (!wrapper) {
        if (this.dataset.productUrl) window.location.href = this.dataset.productUrl;
        return;
      }

      const featuredUpsellsInner = wrapper.closest('.featured-upsells__inner');
      document.querySelectorAll('quick-view-drawer').forEach((container) => {
        const drawerContent = container.querySelector('.quick-view__content');
        if (drawerContent) drawerContent.innerHTML = '';

        const quickView = container.querySelector('quick-view');
        if (quickView) quickView.resetLoadState();
      });

      const drawer = wrapper.querySelector('quick-view-drawer');
      if (drawer) {
        if (featuredUpsellsInner) {
          featuredUpsellsInner.classList.add('quick-view-active');
          drawer.addEventListener('close', () => {
            featuredUpsellsInner.classList.remove('quick-view-active');
          }, { once: true });
        }

        drawer.querySelector('summary').click();

        document.dispatchEvent(new CustomEvent('quickview:open', {
          detail: {
            productUrl: this.dataset.productUrl
          }
        }));
      } else if (this.dataset.productUrl) {
        window.location.href = this.dataset.productUrl;
      }
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

class QuickView extends HTMLElement {
  constructor() {
    super();
    this._loaded = false;
    this._observer = null;
  }

  connectedCallback() {
    this._observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this._observer.observe(this);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  resetLoadState() {
    this._loaded = false;
    this._observer?.observe(this);
  }

  getContentElement(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (
      doc.querySelector('.quick-view--modal .quick-view__content') ||
      doc.querySelector('.quick-view__content')
    );
  }

  /**
   * Prefer the stable section file name (works because main-product-modal is
   * statically rendered from product.quick-view.liquid). Fall back to the
   * layout-less alternate template if needed.
   */
  buildUrls(productUrl, variantId) {
    // Keeps the card's colour selected in the modal (colour cards link to ?variant=)
    const variantParam = variantId ? `&variant=${encodeURIComponent(variantId)}` : '';

    return [
      `${productUrl}?section_id=main-product-modal${variantParam}`,
      `${productUrl}?view=quick-view${variantParam}`
    ];
  }

  async fetchModalHtml(productUrl, variantId) {
    let lastHtml = '';

    for (const url of this.buildUrls(productUrl, variantId)) {
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) continue;

      const html = await response.text();
      lastHtml = html;

      if (html.includes('quick-view__content')) {
        return html;
      }
    }

    return lastHtml;
  }

  /**
   * Variant bar column/height JS often runs in connectedCallback before
   * injected CSS, fonts, or the drawer cover settle. Re-apply until layout
   * has real measurements.
   */
  applyVariantBarLayout(root) {
    if (!root) return;

    if (typeof theme?.applyVariantBarLayout === 'function') {
      theme.applyVariantBarLayout(root);
    } else if (typeof updateVariantOptionCellWidths === 'function') {
      updateVariantOptionCellWidths(root);
      equalizeVariantBadgeHeights(root);
      equalizeVariantTextHeights(root);
    }

    root.querySelectorAll('variant-radios').forEach((el) => {
      if (typeof el.updateVariantBarLayout === 'function') {
        el.updateVariantBarLayout();
      } else if (typeof el.scheduleVariantBarLayout === 'function') {
        el.scheduleVariantBarLayout(true);
      }
    });
  }

  scheduleVariantBarLayoutPasses(root) {
    if (!root) return;

    const run = () => this.applyVariantBarLayout(root);

    run();

    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(run);
    }

    // Cover hide / late stylesheet / drawer open animation
    [50, 200, 450, 700].forEach((ms) => setTimeout(run, ms));
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting || this._loaded) return;

    // Firefox can report intersection for nodes inside closed <details>
    if (!this.closest('details')?.open) return;

    const drawerContent = this.querySelector('.quick-view__content');
    if (!drawerContent || !this.dataset.productUrl) return;
    const url = new URL(this.dataset.productUrl, window.location.origin);
    const productUrl = url.pathname;
    const variantId = url.searchParams.get('variant');

    this._loaded = true;
    observer.unobserve(this);

    this.fetchModalHtml(productUrl, variantId)
      .then(async (html) => {
        const productElement = this.getContentElement(html);

        if (!productElement) {
          console.error('Quick view: could not find .quick-view__content', {
            productUrl,
            length: html.length,
            hasString: html.includes('quick-view__content')
          });
          this.resetLoadState();
          return;
        }

        this.setInnerHTML(drawerContent, productElement.innerHTML);
        await this.ensureStylesheets(html);

        // First layout pass once markup + stylesheets are in place
        this.scheduleVariantBarLayoutPasses(drawerContent);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        setTimeout(() => {
          drawerContent.classList.add('hide-cover');
          // Cover removal can change available width / overflow for the grid
          this.scheduleVariantBarLayoutPasses(drawerContent);

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: { productUrl: this.dataset.productUrl }
          }));
        }, 500);
      })
      .catch((error) => {
        console.error(error);
        this.resetLoadState();
      });
  }

  ensureStylesheets(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pending = [];

    doc.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;

      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = href;

      pending.push(new Promise((resolve) => {
        newLink.addEventListener('load', resolve, { once: true });
        newLink.addEventListener('error', resolve, { once: true });
        // Safety if neither fires (cached edge cases)
        setTimeout(resolve, 1500);
      }));

      document.head.appendChild(newLink);
    });

    return pending.length ? Promise.all(pending) : Promise.resolve();
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);
